import { useEffect, useState, type ReactNode } from 'react';
import { m } from 'motion/react';
import { Link, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Bookmark,
  ClipboardList,
  ListTodo,
  Rocket,
  FolderKanban,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  TriangleAlert,
  Target,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { projectInitial } from '../../lib/assigned-queue';
import { projectWorkPath, useProjectIdFromPath } from '../../lib/project-path';
import { isServiceRequestArea } from '../../lib/service-request-views';
import { isWorkspaceProject } from '../../lib/workspace-projects';
import { INITIATIVES_PATH } from '../../lib/initiatives';
import { cn } from '../../lib/utils';
import type { ProjectDto } from '../../lib/work-types';
import { useSession } from '../session-context';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import WikiSidebarNav from '../wiki/WikiSidebarNav';
import {
  PROJECT_TABS,
  visibleSettingsLinks,
  sidebarIconClass,
  sidebarItemClass,
  sidebarLabelClass,
} from './nav-item';
import { useMyApprovals } from './MyApprovals';
import { useOverlays } from './overlay-context';
import PulseLogo from '../PulseLogo';
import { COMPLETED_TARGET_ATTR } from '../../lib/mark-done';

function NavIcon({ icon: Icon, active }: { icon: LucideIcon; active?: boolean }) {
  return <Icon className={sidebarIconClass(Boolean(active))} aria-hidden />;
}

/** When the rail is folded the label moves into a tooltip on the right. */
function RailTooltip({ label, collapsed, children }: { label: string; collapsed: boolean; children: ReactNode }) {
  if (!collapsed) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

type SidebarItemProps = {
  icon: LucideIcon;
  label: string;
  collapsed: boolean;
  active?: boolean;
  to?: string;
  onClick?: () => void;
  /** Count shown after the label, e.g. approvals waiting on you; hidden when 0. */
  badge?: number;
  badgeLabel?: string;
  /** Where marked-done work lands; see `flyToCompleted`. */
  completedTarget?: string;
};

/** A nav link (`to`) or action (`onClick`) that folds to an icon with a tooltip. */
function SidebarItem({
  icon,
  label,
  collapsed,
  active = false,
  to,
  onClick,
  badge = 0,
  badgeLabel,
  completedTarget,
}: SidebarItemProps) {
  const className = cn(sidebarItemClass(active), collapsed && 'relative justify-center px-0');
  const targetProps = completedTarget ? { [COMPLETED_TARGET_ATTR]: completedTarget } : {};
  const content = (
    <>
      {active ? (
        <m.span
          layoutId="sidebar-active"
          aria-hidden
          className="absolute inset-0 -z-10 rounded-full bg-[hsl(var(--brand-blue))] shadow-[0_6px_16px_-8px_hsl(var(--brand-blue)/0.9)]"
          transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.7 }}
        />
      ) : null}
      <NavIcon icon={icon} active={active} />
      <span className={cn('truncate', collapsed && 'sr-only')}>{label}</span>
      {badge > 0 ? (
        collapsed ? (
          <span className="absolute right-1.5 top-1 h-2 w-2 rounded-full bg-brand-ice" aria-label={badgeLabel ?? String(badge)} />
        ) : (
          <span
            className={cn(
              'ml-auto rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums',
              active ? 'bg-white/20 text-white' : 'bg-brand-ice text-brand-navy',
            )}
            aria-label={badgeLabel ?? String(badge)}
          >
            {badge}
          </span>
        )
      ) : null}
    </>
  );
  return (
    <RailTooltip label={label} collapsed={collapsed}>
      {to ? (
        <Link to={to} onClick={onClick} className={className} aria-current={active ? 'page' : undefined} {...targetProps}>
          {content}
        </Link>
      ) : (
        <button type="button" onClick={onClick} className={className}>
          {content}
        </button>
      )}
    </RailTooltip>
  );
}

type Props = {
  onNavigate?: () => void;
  /** Desktop rail state; the mobile drawer never folds. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

type SidebarView = {
  id: string;
  name: string;
  is_system: boolean;
};

function projectTab(pathname: string): string {
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.endsWith('/completed') || pathname.endsWith('/archives')) return 'completed';
  if (pathname.includes('/views')) return '';
  if (pathname.endsWith('/board') || pathname.endsWith('/tasks') || pathname.includes('/work-items')) {
    return 'tasks';
  }
  return '';
}

export default function AppSidebar({ onNavigate, collapsed = false, onToggleCollapse }: Props) {
  const location = useLocation();
  const { isAdmin, isOrgAdmin } = useSession();
  const { openCreateView } = useOverlays();
  const projectId = useProjectIdFromPath();
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [hideStream, setHideStream] = useState(false);
  const [workspaceViews, setWorkspaceViews] = useState<SidebarView[]>([]);
  const [savedViews, setSavedViews] = useState<SidebarView[]>([]);
  const inWiki = location.pathname === '/wiki' || location.pathname.startsWith('/wiki/');
  const inServiceRequests = isServiceRequestArea(location.pathname);

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      setHideStream(false);
      return;
    }
    let cancelled = false;
    void apiFetch<ProjectDto>(v1Path(`/projects/${projectId}`))
      .then((dto) => {
        if (cancelled) return;
        setHideStream(isWorkspaceProject(dto));
        setProject(dto);
      })
      .catch(() => {
        if (!cancelled) setProject(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    void apiFetch<{ items: SidebarView[] }>(v1Path('/views'))
      .then((res) => {
        if (!cancelled) {
          setWorkspaceViews(res.items.filter((view) => !view.is_system));
        }
      })
      .catch(() => {
        if (!cancelled) setWorkspaceViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!projectId) {
      setSavedViews([]);
      return;
    }
    let cancelled = false;
    void apiFetch<{ items: SidebarView[] }>(v1Path(`/projects/${projectId}/views`))
      .then((res) => {
        if (!cancelled) {
          setSavedViews(res.items.filter((view) => !view.is_system));
        }
      })
      .catch(() => {
        if (!cancelled) setSavedViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, location.pathname]);

  const approvals = useMyApprovals(location.pathname);
  const resourcesActive = location.pathname.startsWith('/resources');
  const projectsActive = location.pathname === '/projects';
  const wikiActive = inWiki;
  const releasesActive = location.pathname === '/releases' || location.pathname.startsWith('/releases/');
  const myWorkActive = location.pathname === '/my-work';
  const issuesActive = ['/incidents', '/incidents/new', '/problems', '/problems/new'].includes(location.pathname);
  const settingsActive = location.pathname.startsWith('/settings') || location.pathname.startsWith('/admin/users');
  const tab = projectId ? projectTab(location.pathname) : null;

  // Section captions carry no meaning as icons; when folded they become dividers.
  const sectionLabel = (text: string, spaced = false) =>
    collapsed ? (
      <hr className={cn('mx-3 border-border', spaced ? 'my-2' : 'mb-1')} aria-hidden />
    ) : (
      <p className={cn(sidebarLabelClass, spaced && 'pt-4')}>{text}</p>
    );

  return (
    <div className="brand-rail flex h-full flex-col">
      <div className={cn('py-4', collapsed ? 'flex justify-center px-0' : 'px-3')}>
        <Link to="/my-work" onClick={onNavigate} className="inline-flex" aria-label="My work">
          <PulseLogo variant={collapsed ? 'mark' : 'wordmark'} />
        </Link>
      </div>
      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-2 pb-4" aria-label="Workspace">
        <div className="space-y-0.5">
          <SidebarItem
            icon={ListTodo}
            label="My work"
            to="/my-work"
            onClick={onNavigate}
            active={myWorkActive || location.pathname === '/'}
            collapsed={collapsed}
            badge={approvals?.length ?? 0}
            badgeLabel={`${approvals?.length ?? 0} waiting for your approval`}
          />
          <SidebarItem
            icon={Target}
            label="Initiatives"
            to={INITIATIVES_PATH}
            onClick={onNavigate}
            active={location.pathname === INITIATIVES_PATH || location.pathname.startsWith(`${INITIATIVES_PATH}/`)}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={ClipboardList}
            label="Service requests"
            to="/service-requests"
            onClick={onNavigate}
            active={inServiceRequests}
            collapsed={collapsed}
          />
          <SidebarItem icon={FolderKanban} label="Streams" to="/projects" onClick={onNavigate} active={projectsActive} collapsed={collapsed} />
          {sectionLabel('More', true)}
          <SidebarItem icon={TriangleAlert} label="Issues" to="/incidents" onClick={onNavigate} active={issuesActive} collapsed={collapsed} />
          <SidebarItem icon={Rocket} label="Releases" to="/releases" onClick={onNavigate} active={releasesActive} collapsed={collapsed} />
          <SidebarItem icon={UsersRound} label="People" to="/resources" onClick={onNavigate} active={resourcesActive} collapsed={collapsed} />
          <SidebarItem icon={BookOpen} label="Wiki" to="/wiki" onClick={onNavigate} active={wikiActive} collapsed={collapsed} />
          {inWiki && !collapsed ? <WikiSidebarNav onNavigate={onNavigate} /> : null}
          {sectionLabel('Saved views', true)}
          {workspaceViews.map((view) => (
            <SidebarItem
              key={view.id}
              icon={Bookmark}
              label={view.name}
              to={`/views/${view.id}`}
              onClick={onNavigate}
              active={location.pathname === `/views/${view.id}`}
              collapsed={collapsed}
            />
          ))}
          <SidebarItem
            icon={Plus}
            label="New view"
            collapsed={collapsed}
            onClick={() => {
              onNavigate?.();
              openCreateView();
            }}
          />
        </div>
        {projectId && !hideStream ? (
          <div className="space-y-0.5">
            <RailTooltip label={project?.name ?? 'Stream'} collapsed={collapsed}>
              <div className={cn('flex items-center gap-2 pb-1.5', collapsed ? 'justify-center' : 'px-2.5')}>
                {project ? (
                  <span
                    aria-hidden
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-[11px] font-semibold text-muted-foreground"
                  >
                    {projectInitial(project.name, project.identifier)}
                  </span>
                ) : null}
                <span
                  className={cn('truncate text-sm font-semibold text-foreground', collapsed && 'sr-only')}
                  title={collapsed ? undefined : project?.name}
                >
                  {project?.name ?? 'Stream'}
                </span>
              </div>
            </RailTooltip>
            {PROJECT_TABS.map((item) => (
              <SidebarItem
                key={item.suffix}
                icon={item.icon}
                label={item.label}
                to={
                  item.suffix === 'tasks'
                    ? projectWorkPath(projectId)
                    : `/projects/${projectId}/${item.suffix}`
                }
                onClick={onNavigate}
                active={tab === item.suffix}
                collapsed={collapsed}
                completedTarget={item.suffix === 'completed' ? `project:${projectId}` : undefined}
              />
            ))}
            {sectionLabel('Stream views', true)}
            {savedViews.map((view) => (
              <SidebarItem
                key={view.id}
                icon={Bookmark}
                label={view.name}
                to={`/projects/${projectId}/views/${view.id}`}
                onClick={onNavigate}
                active={location.pathname === `/projects/${projectId}/views/${view.id}`}
                collapsed={collapsed}
              />
            ))}
            <SidebarItem
              icon={Plus}
              label="New view"
              collapsed={collapsed}
              onClick={() => {
                onNavigate?.();
                openCreateView({ projectId });
              }}
            />
          </div>
        ) : null}
        {settingsActive ? (
          <div className="space-y-0.5">
            {sectionLabel('Settings')}
            {visibleSettingsLinks(isOrgAdmin, isAdmin).map((link) => (
              <SidebarItem
                key={link.to}
                icon={link.icon}
                label={link.label}
                to={link.to}
                onClick={onNavigate}
                active={link.prefix ? location.pathname.startsWith(link.to) : location.pathname === link.to}
                collapsed={collapsed}
              />
            ))}
          </div>
        ) : null}
      </nav>
      {onToggleCollapse ? (
        <div className="border-t px-2 py-2">
          <SidebarItem
            icon={collapsed ? PanelLeftOpen : PanelLeftClose}
            label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            collapsed={collapsed}
            onClick={onToggleCollapse}
          />
        </div>
      ) : null}
    </div>
  );
}
